import { forwardRef } from "react";
import { Loader2, Search } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { MailThread } from "../../../lib/mail";
import type { CrmLabel } from "../../../lib/crmLabels";
import type { BulkThreadPatch } from "../../../lib/mailActions";
import { ContextMenu, useContextMenu } from "../../shell/ContextMenu";
import { DensityToggle } from "../_shared/DensityToggle";
import { ReadingPaneToggle } from "../_shared/ReadingPaneToggle";
import type { ReadingPane } from "../_shared/readingPane";
import { RailTrigger } from "../_shared/workMobile";
import type { WorkDensity } from "../_shared/density";
import { ThreadFilterMenu } from "./ThreadFilterMenu";
import { ThreadRow } from "./ThreadRow";
import { ThreadRowItems } from "./ThreadRowItems";
import { mailFiltersActive, type MailFilters } from "./filters";
import { GroupToggle, MailGroupItems, MailGroups, useGroupedMail, type MailGroup } from "./grouped";

/**
 * What the list's one menu was raised on: a conversation, or a folded group
 * standing for several. One menu rather than two, because each would close only
 * on its own outside click, and a long press on a phone - which sends no
 * mousedown - could leave both open at once.
 */
type MenuTarget = { kind: "thread"; thread: MailThread } | { kind: "group"; group: MailGroup };

/**
 * The middle pane: the search head, the scrolling column of conversations, and
 * the one context menu they share.
 *
 * A row itself is `ThreadRow` - it is where the two densities differ in markup
 * rather than in numbers, and keeping that branch out of here leaves this file
 * about the pane. Who a row belongs to comes from the best name available, in
 * order: the matched company, then the linked client, then the sender's own
 * name, then the bare address (`threadWho`). An operator triaging a morning's
 * mail is looking for a company, and falling straight through to `noreply@`
 * would hide the one thing they scan for.
 */
export const ThreadList = forwardRef<
  HTMLDivElement,
  {
    rows: MailThread[];
    openId: string | null;
    loading: boolean;
    q: string;
    onQ: (v: string) => void;
    filters: MailFilters;
    onFilters: (next: MailFilters) => void;
    onOpen: (id: string) => void;
    campaignName: (id: string | null) => string | undefined;
    labels: CrmLabel[];
    onReply: (thread: MailThread) => void;
    onPatch: (id: string, body: Record<string, unknown>) => Promise<void>;
    onDelete: (thread: MailThread) => void;
    /** Every conversation in a folded group at once. */
    onPatchMany: (ids: string[], patch: BulkThreadPatch) => Promise<void>;
    onDeleteMany: (group: MailGroup) => void;
    density: WorkDensity;
    onDensity: () => void;
    readingPane: ReadingPane;
    onReadingPane: (next: ReadingPane) => void;
    /** Opens the folder rail. Only reachable on a phone, where it is a sheet. */
    onRail: () => void;
    /** In the combined inbox: the mailbox a conversation is in, as a short tag. */
    mailboxTag?: (thread: MailThread) => string;
  }
>(function ThreadList(
  {
    rows,
    openId,
    loading,
    q,
    onQ,
    filters,
    onFilters,
    onOpen,
    campaignName,
    labels,
    onReply,
    onPatch,
    onDelete,
    onPatchMany,
    onDeleteMany,
    density,
    onDensity,
    readingPane,
    onReadingPane,
    onRail,
    mailboxTag,
  },
  ref,
) {
  const t = useT();
  const menu = useContextMenu<MenuTarget>();
  const threadMenu = (thread: MailThread) => menu.rowProps({ kind: "thread", thread });
  // Owned here rather than passed down from the section, unlike the density and
  // the reading pane: this is a property of the list pane alone, nothing else on
  // the screen asks about it, and the section is already the biggest file here.
  const [grouped, toggleGrouped] = useGroupedMail();

  return (
    // `crm-work-list` is what the phone layout hides when a conversation is
    // open. It is on the card rather than on a wrapper because the wrapper is
    // the grid cell, and the grid is what does the hiding.
    <div ref={ref} className="crm-card crm-work-col crm-work-list">
      <div className="crm-pane-head">
        <RailTrigger label={t("mail.folders")} onOpen={onRail} />
        <label className="crm-pane-search">
          <Search className="size-4" />
          <input
            type="search"
            value={q}
            onChange={(e) => onQ(e.target.value)}
            placeholder={t("mail.searchPlaceholder")}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        {loading ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
        <GroupToggle grouped={grouped} onToggle={toggleGrouped} />
        <DensityToggle density={density} onToggle={onDensity} ns="mail" />
        <ReadingPaneToggle pane={readingPane} onPane={onReadingPane} />
        <ThreadFilterMenu filters={filters} onFilters={onFilters} />
      </div>

      {/* `is-grouped` widens every row by the chevron's lane, plain rows too,
          so the avatars stay on one line. See `crm-folded-rows.css`. */}
      <div className={cn("crm-pane-scroll", grouped && "is-grouped")}>
        {rows.length === 0 ? (
          // A filtered folder that came back empty is not an empty folder, and
          // saying so is the whole reason the filter button carries a dot.
          <div className="crm-pane-empty">
            {q ? t("mail.noHits") : mailFiltersActive(filters) ? t("mail.filter.noHits") : t("mail.emptyFolder")}
          </div>
        ) : grouped ? (
          <MailGroups
            rows={rows}
            openId={openId}
            labels={labels}
            campaignName={campaignName}
            onOpen={onOpen}
            rowMenuProps={threadMenu}
            groupMenuProps={(group) => menu.rowProps({ kind: "group", group })}
          />
        ) : (
          rows.map((th) => (
            <ThreadRow
              key={th.id}
              thread={th}
              open={th.id === openId}
              labels={labels}
              campaign={campaignName(th.campaignId)}
              onOpen={onOpen}
              menuProps={threadMenu(th)}
              mailbox={mailboxTag?.(th)}
            />
          ))
        )}
      </div>

      {/* One panel for the whole list, holding whichever row was right-clicked.
          It renders into the body, so the pane's clipped overflow cannot cut it
          off the way an absolutely positioned panel in the last row would be. */}
      <ContextMenu menu={menu} label={t(menu.at?.target.kind === "group" ? "mail.group.menu" : "email.row.menu")}>
        {(target, close) =>
          target.kind === "group" ? (
            <MailGroupItems
              group={target.group}
              close={close}
              onOpen={onOpen}
              onSearch={onQ}
              onPatchMany={onPatchMany}
              onDeleteMany={onDeleteMany}
            />
          ) : (
            <ThreadRowItems
              thread={target.thread}
              labels={labels}
              close={close}
              onOpen={onOpen}
              onReply={onReply}
              onSearch={onQ}
              onPatch={onPatch}
              onDelete={onDelete}
            />
          )
        }
      </ContextMenu>
    </div>
  );
});
